import { useEffect, useRef, useState } from 'react';

type UseWebSocketParams = {
  onClose: () => void;
  onError?: (error: Event) => void;
  onMessage?: (message: string) => void;
  url: string;
};

export function useWebSocket({ onClose, onError, onMessage, url }: UseWebSocketParams) {
  const callbacksRef = useRef({ onError, onMessage, onClose });
  const [wsState, setWsState] = useState<number>(WebSocket.CLOSED);
  const wsRef = useRef<WebSocket>();
  const sendMessageRef = useRef((message: string) => {
    if (!wsRef.current) {
      throw Error('WebSocket is not initialized yet!');
    }

    wsRef.current.send(message);
  });

  const isReady = wsState === WebSocket.OPEN;

  useEffect(() => {
    callbacksRef.current = {
      onError,
      onMessage,
      onClose,
    };
  }, [onError, onMessage, onClose]);

  useEffect(() => {
    const ws = new WebSocket(url);

    wsRef.current = ws;

    setWsState(ws.readyState);

    function onErrorEvent(event: Event) {
      callbacksRef.current.onError?.(event);
    }

    function onOpenEvent() {
      setWsState(ws.readyState);
    }

    function onMessageEvent(event: MessageEvent<string>) {
      callbacksRef.current.onMessage?.(event.data);
    }

    function onCloseEvent() {
      callbacksRef.current.onClose();
      setWsState(ws.readyState);
    }

    ws.addEventListener('error', onErrorEvent);
    ws.addEventListener('open', onOpenEvent);
    ws.addEventListener('message', onMessageEvent);
    ws.addEventListener('close', onCloseEvent);

    return () => {
      ws.close();
      ws.removeEventListener('error', onErrorEvent);
      ws.removeEventListener('open', onOpenEvent);
      ws.removeEventListener('message', onMessageEvent);
      ws.removeEventListener('close', onCloseEvent);
    };
  }, [url]);

  return { sendMessage: sendMessageRef.current, isReady } as const;
}
